import type { CompetitionStatus, LifecycleStatus } from '../types/competition';
import type { TrophyStatus } from '../types/trophy';

/**
 * How a place's life cycle reads on screen: the badge it wears and the key its
 * label comes from.
 *
 * One module because the same statuses are drawn in five places — the circuit's
 * page, its stages list, a gym's events, the competition's own page and the
 * dashboard — and three copies of the map had already started to drift before
 * this existed.
 */
export type StatusBadgeVariant = 'default' | 'secondary' | 'outline';

export interface StatusBadge {
  variant: StatusBadgeVariant;
  labelKey: string;
}

/**
 * Filled for the two phases something is actually happening in, outlined while
 * it is still ahead, muted once it is behind. Draft is outlined rather than
 * loud: nobody but its own staff ever sees it.
 */
export const STATUS_VARIANT: Record<LifecycleStatus, StatusBadgeVariant> = {
  draft: 'outline',
  scheduled: 'outline',
  open: 'default',
  in_progress: 'default',
  closed: 'secondary',
  archived: 'secondary',
};

/**
 * What to put on a competition's badge.
 *
 * The one place the phase is not the whole story: a competition stays `open`
 * after the hour its organiser set, and saying "entries open" beside a button
 * that is no longer there would be a lie the page tells about itself. So the
 * badge reads the hour too — the status is still `open`, the label is not.
 *
 * `entriesBlocked` is the same lie from the other direction: the hour has not
 * passed, and the desk still takes nobody because its organiser has not signed
 * (`StageParticipation.entry_block`). "On hold" rather than "closed", because
 * closed is something the organiser did on purpose and this is not.
 */
export function competitionBadge(
  status: CompetitionStatus,
  entriesOpen?: boolean,
  entriesBlocked?: boolean
): StatusBadge {
  if (status === 'open' && entriesBlocked) {
    return { variant: 'outline', labelKey: 'lifecycleStatus.competition.entriesOnHold' };
  }

  if (status === 'open' && entriesOpen === false) {
    return { variant: 'outline', labelKey: 'lifecycleStatus.competition.entriesClosed' };
  }

  return { variant: STATUS_VARIANT[status], labelKey: competitionStatusKey(status) };
}

export function trophyBadge(status: TrophyStatus): StatusBadge {
  return { variant: STATUS_VARIANT[status], labelKey: trophyStatusKey(status) };
}

/**
 * Which moves are worth a question first: the ones that change who can see the
 * place at all, and the ones that drag a circuit's stages with them. Moving
 * between the announced phases is not one of them — that is the ordinary work
 * of running an event.
 */
export function needsConfirmation(from: LifecycleStatus, to: LifecycleStatus): boolean {
  return from === 'draft' || to === 'draft' || to === 'closed' || to === 'archived';
}

/**
 * A competition's label and a circuit's are separate keys on purpose: in
 * Italian a gara is "Conclusa" and a trofeo "Chiuso", and one shared list
 * would get one of the two wrong.
 */
export function competitionStatusKey(status: CompetitionStatus): string {
  return `lifecycleStatus.competition.${status}`;
}

export function trophyStatusKey(status: TrophyStatus): string {
  return `lifecycleStatus.trophy.${status}`;
}
